/**
 * BugReportModal Component
 *
 * Comprehensive bug/issue reporting modal for Presidio CC Tools.
 * Allows users to submit bugs, feature requests, and questions with
 * file attachments, automatic user context, and environment information.
 */

import React, { useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Select,
  FileInput,
  Button,
  Group,
  Text,
  Badge,
  Alert,
  Paper,
  List,
  Divider,
  Box,
  Loader
} from '@mantine/core';
import {
  IconBug,
  IconUpload,
  IconTrash,
  IconAlertCircle,
  IconCheck,
  IconBulb,
  IconHelp,
  IconFileDescription
} from '@tabler/icons-react';
import { useAuth } from '../shared/contexts/AuthContext';
import { usePlatform } from '../shared/contexts/PlatformContext';
import bugReportService from '../shared/services/bugReportService';

/**
 * BugReportModal Component
 */
const BugReportModal = ({ opened, onClose, onSuccess }) => {
  const { user, getApiToken } = useAuth();
  const { currentPlatform } = usePlatform();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    platform: currentPlatform || 'aws',
    issueType: 'bug',
    severity: 'medium',
    screenshots: [],
    templates: []
  });

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Issue type options
  const issueTypeOptions = [
    { value: 'bug', label: 'Bug Report', icon: IconBug },
    { value: 'feature-request', label: 'Feature Request', icon: IconBulb },
    { value: 'question', label: 'Question', icon: IconHelp },
    { value: 'other', label: 'Other', icon: IconFileDescription }
  ];

  // Severity options
  const severityOptions = [
    { value: 'low', label: 'Low - Minor issue, workaround available', color: 'blue' },
    { value: 'medium', label: 'Medium - Moderate impact', color: 'yellow' },
    { value: 'high', label: 'High - Significant impact', color: 'orange' },
    { value: 'critical', label: 'Critical - Blocking work', color: 'red' }
  ];

  /**
   * Handle form field changes
   */
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  /**
   * Handle file uploads
   */
  const handleFileUpload = (field, files) => {
    if (!files) return;

    const fileArray = Array.isArray(files) ? files : [files];
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ...fileArray]
    }));
  };

  /**
   * Remove uploaded file
   */
  const removeFile = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Please provide a title for your report');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Please provide a description of the issue');
      return false;
    }
    return true;
  };

  /**
   * Submit bug report
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      // Check if service is configured
      if (!bugReportService.isConfigured()) {
        throw new Error('Bug tracker service is not configured. Please contact your administrator.');
      }

      // Prepare report data
      const reportData = {
        ...formData,
        userName: user?.name || 'Anonymous',
        userEmail: user?.email || '',
        platform: formData.platform || 'unknown',
        userAgent: navigator.userAgent,
        currentUrl: window.location.href
      };

      // Get API token for authorization
      let token = null;
      try {
        token = await getApiToken();
      } catch (tokenError) {
        console.warn('Could not acquire API token for bug report:', tokenError);
      }

      // Submit report
      const result = await bugReportService.submitBugReport(reportData, token);

      // Show success state
      setSuccess(true);

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal after delay
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError(err.message || 'Failed to submit bug report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Reset form and close modal
   */
  const handleClose = () => {
    if (submitting) return;

    setFormData({
      title: '',
      description: '',
      platform: currentPlatform || 'aws',
      issueType: 'bug',
      severity: 'medium',
      screenshots: [],
      templates: []
    });
    setError(null);
    setSuccess(false);
    onClose();
  };

  /**
   * Get issue type icon
   */
  const getIssueTypeIcon = () => {
    const selected = issueTypeOptions.find(opt => opt.value === formData.issueType);
    const IconComponent = selected?.icon || IconBug;
    return <IconComponent size={20} />;
  };

  /**
   * Render file list
   */
  const renderFileList = (files, field) => {
    if (files.length === 0) return null;

    return (
      <Paper p="sm" withBorder>
        <List size="sm" gap="xs">
          {files.map((file, index) => (
            <List.Item key={index}>
              <Group justify="space-between">
                <Group gap="xs">
                  <IconUpload size={14} />
                  <Text size="xs" truncate style={{ maxWidth: 200 }}>
                    {file.name}
                  </Text>
                  <Badge size="xs" variant="light">
                    {(file.size / 1024).toFixed(1)} KB
                  </Badge>
                </Group>
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => removeFile(field, index)}
                  leftSection={<IconTrash size={14} />}
                >
                  Remove
                </Button>
              </Group>
            </List.Item>
          ))}
        </List>
      </Paper>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          {getIssueTypeIcon()}
          <Text fw={600}>Report Issue or Request Feature</Text>
        </Group>
      }
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        {/* Success State */}
        {success && (
          <Alert
            icon={<IconCheck size={16} />}
            title="Report Submitted Successfully"
            color="green"
          >
            Thank you for your feedback! Your report has been submitted and the team has been notified.
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* User Context Display */}
        <Paper p="md" bg="gray.0" withBorder>
          <Text size="xs" fw={500} mb="xs" c="dimmed">
            Your Information
          </Text>
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="xs" fw={500}>Name:</Text>
              <Text size="xs">{user?.name || 'Anonymous'}</Text>
            </Group>
            <Group gap="xs">
              <Text size="xs" fw={500}>Email:</Text>
              <Text size="xs">{user?.email || 'Not provided'}</Text>
            </Group>
          </Stack>
        </Paper>

        <Divider />

        {/* Platform Selection */}
        <Select
          label="Platform"
          placeholder="Select platform"
          data={[
            { value: 'aws', label: 'AWS Connect' },
            { value: 'cisco', label: 'Cisco WxCC' },
            { value: 'five9', label: 'Five9' },
            { value: 'satools', label: 'SA Tools' }
          ]}
          value={formData.platform}
          onChange={(value) => handleChange('platform', value)}
          required
          disabled={submitting || success}
        />

        {/* Issue Type Selection */}
        <Select
          label="Issue Type"
          placeholder="Select issue type"
          data={issueTypeOptions.map(opt => ({
            value: opt.value,
            label: opt.label
          }))}
          value={formData.issueType}
          onChange={(value) => handleChange('issueType', value)}
          required
          disabled={submitting || success}
        />

        {/* Severity Selection (only for bugs) */}
        {formData.issueType === 'bug' && (
          <Select
            label="Severity"
            placeholder="Select severity level"
            data={severityOptions.map(opt => ({
              value: opt.value,
              label: opt.label
            }))}
            value={formData.severity}
            onChange={(value) => handleChange('severity', value)}
            required
            disabled={submitting || success}
          />
        )}

        {/* Title Input */}
        <TextInput
          label="Title"
          placeholder="Brief summary of the issue or request"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          required
          disabled={submitting || success}
          maxLength={200}
        />

        {/* Description Textarea */}
        <Textarea
          label="Description"
          placeholder="Detailed description of the issue, steps to reproduce, or feature request details..."
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          required
          disabled={submitting || success}
          minRows={5}
          maxRows={10}
          autosize
        />

        {/* Screenshot Upload */}
        <Box>
          <FileInput
            label="Screenshots"
            placeholder="Upload screenshots (optional)"
            multiple
            accept="image/*"
            onChange={(files) => handleFileUpload('screenshots', files)}
            disabled={submitting || success}
            leftSection={<IconUpload size={16} />}
          />
          <Text size="xs" c="dimmed" mt="xs">
            Attach screenshots showing the issue (PNG, JPG, GIF)
          </Text>
          {renderFileList(formData.screenshots, 'screenshots')}
        </Box>

        {/* Template/File Upload */}
        <Box>
          <FileInput
            label="Files or Templates"
            placeholder="Upload related files (optional)"
            multiple
            onChange={(files) => handleFileUpload('templates', files)}
            disabled={submitting || success}
            leftSection={<IconUpload size={16} />}
          />
          <Text size="xs" c="dimmed" mt="xs">
            Attach any relevant files, templates, or documents
          </Text>
          {renderFileList(formData.templates, 'templates')}
        </Box>

        <Divider />

        {/* Action Buttons */}
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            leftSection={submitting ? <Loader size={16} /> : getIssueTypeIcon()}
            onClick={handleSubmit}
            loading={submitting}
            disabled={success}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default BugReportModal;
